import React, { createContext, forwardRef, useContext } from 'react'
import { cn } from '@/common/lib/cn/cn'
import './menu.css'

export type MenuVariant = 'default' | 'selectList'

export type GenerateMenuProps = {
    orientation?: 'horizontal' | 'vertical'
    size?: 'sm' | 'md' | 'lg' | 'xl'
    variant?: MenuVariant
} & React.HTMLAttributes<HTMLUListElement>

export const Menu = forwardRef<HTMLUListElement, GenerateMenuProps>(function Menu(
    { children, orientation = 'vertical', size = 'md', variant = 'default', className, ...props },
    ref
) {
    const buttonClasses = cn(
        'menu',
        {
            'menu-horizontal': orientation === 'horizontal',
            'menu-vertical': orientation === 'vertical',
        },
        {
            /* menu-md задаёт свои padding/font — конфликтует с отступами selectList из Figma */
            'menu-md': size === 'md' && variant !== 'selectList',
        },
        {
            'menu-select': variant === 'selectList',
        },
        className
    )

    return (
        <ul ref={ref} className={buttonClasses} {...props}>
            {children}
        </ul>
    )
})

const MenuItemContext = createContext<{ disabled?: boolean; active?: boolean }>({})

export type GenerateMenuItemProps = {
    disabled?: boolean
    active?: boolean
} & React.HTMLAttributes<HTMLLIElement>

export const MenuItem = ({ children, disabled, active, className, ...props }: GenerateMenuItemProps) => {
    const buttonClasses = cn(
        {
            'menu-disabled': disabled,
            'menu-active': active,
        },
        className
    )
    return (
        <MenuItemContext.Provider value={{ disabled, active }}>
            <li className={buttonClasses} {...props}>
                {children}
            </li>
        </MenuItemContext.Provider>
    )
}

export type MenuSelectRowProps = {
    leftSlot?: React.ReactNode
    rightSlot?: React.ReactNode
    children: React.ReactNode
    disabled?: boolean
    href?: string
    className?: string
    /** Select (listbox): `role="option"` и `aria-selected`, без `aria-current` из MenuItem */
    listboxOption?: boolean
} & Omit<React.HTMLAttributes<HTMLElement>, 'children'>

export const MenuSelectRow = forwardRef<HTMLButtonElement | HTMLAnchorElement, MenuSelectRowProps>(
    function MenuSelectRow(
        { leftSlot, rightSlot, children, disabled: disabledProp, className, href, listboxOption, ...rest },
        ref
    ) {
        const ctx = useContext(MenuItemContext)
        const isDisabled = disabledProp ?? ctx.disabled
        const isActive = ctx.active

        const rowClass = cn('menu-select-row', className)
        const isListbox = Boolean(
            listboxOption || (rest as React.ButtonHTMLAttributes<HTMLButtonElement>).role === 'option'
        )

        const content = (
            <>
                {leftSlot != null ? <span className='menu-select-row__left'>{leftSlot}</span> : null}
                <span className='menu-select-row__label'>{children}</span>
                {rightSlot != null ? <span className='menu-select-row__right'>{rightSlot}</span> : null}
            </>
        )

        if (isDisabled || href == null) {
            return (
                <button
                    ref={ref as React.Ref<HTMLButtonElement>}
                    type='button'
                    className={rowClass}
                    disabled={isDisabled}
                    aria-current={isListbox ? undefined : isActive ? 'true' : undefined}
                    {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                >
                    {content}
                </button>
            )
        }

        return (
            <a
                ref={ref as React.Ref<HTMLAnchorElement>}
                href={href}
                className={rowClass}
                aria-current={isListbox ? undefined : isActive ? 'true' : undefined}
                {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
            >
                {content}
            </a>
        )
    }
)

export type MenuDividerProps = React.HTMLAttributes<HTMLLIElement>

export const MenuDivider = ({ className, ...props }: MenuDividerProps) => {
    const dividerClasses = cn('menu-divider', className)
    return <li className={dividerClasses} {...props} />
}
